/**
 * Field map for the fast-food step form mirror.
 *
 * These labels and keys come from the WordPress plugin's postMessage payload.
 * Keep this grouped to match the five visible WordPress form steps so the
 * admin mirror follows the same sequence as the embedded form.
 */

export interface FieldDefinition {
  label: string;
  key: string;
}

export interface FieldSection {
  section: string;
  fields: FieldDefinition[];
}

export const MAIN_SECTIONS: FieldSection[] = [
  {
    section: "Vehicle",
    fields: [
      { label: "Registration", key: "vehicleReg" },
      { label: "Make", key: "vehicleMake" },
      { label: "Model", key: "vehicleModel" },
      { label: "Year", key: "year" },
      { label: "Engine size", key: "engineSize" },
      { label: "ABI code", key: "abiCode" },
      { label: "Insurance start date", key: "insuranceStartDate" },
      { label: "Current policy expiry", key: "currentPolicyExpiryDate" },
      { label: "Has no-claims bonus", key: "isNoClaimsBonus" },
      { label: "No-claims bonus expiry", key: "noClaimsBonusYears" },
      { label: "No-claims bonus earned on", key: "typeNoClaimsBonusEarnedOn" },
      { label: "No-claims bonus years", key: "noClaimsBonus" },
      { label: "Vehicle purchased", key: "isVehiclePurchased" },
      { label: "Purchase date", key: "purchaseDate" },
      { label: "Current value", key: "currentValue" },
      { label: "Vehicle owner", key: "vehicleOwner" },
      { label: "Registered keeper", key: "registeredKeeper" },
      { label: "Other registered keeper", key: "registeredKeeperOtherOptions" },
      { label: "Number of seats", key: "numberOfSeats" },
      { label: "Transmission", key: "transmission" },
      { label: "Number of doors", key: "numberOfDoors" },
      { label: "Fuel type", key: "fuelType" },
      { label: "Imported", key: "imported" },
      { label: "Right-hand drive", key: "rhd" },
      { label: "Body type", key: "body" },
      { label: "Modified", key: "vehicleModifications" },
      { label: "How it is modified", key: "howVehicleIsModified" },
    ],
  },
  {
    section: "Address",
    fields: [
      { label: "Postcode", key: "postcode" },
      { label: "Address line 1", key: "addressLine1" },
      { label: "Address line 2", key: "addressLine2" },
      { label: "Town or city", key: "townOrCity" },
      { label: "County", key: "county" },
      { label: "Kept at same address", key: "vehicleKeptAtSameAddress" },
      { label: "Overnight postcode", key: "postCodeOfOvernightLocation" },
      { label: "Overnight address line 1", key: "overnightAddressLine1" },
      { label: "Overnight address line 2", key: "overnightAddressLine2" },
      { label: "Overnight town or city", key: "overnightTownOrCity" },
      { label: "Overnight county", key: "overnightCounty" },
      { label: "Overnight location", key: "whereVehicleIsKeptOvernight" },
      { label: "County court judgements", key: "countyCourtJudgements" },
      { label: "Windscreen cover", key: "windscreenCover" },
    ],
  },
  {
    section: "Driver",
    fields: [
      { label: "Title", key: "title" },
      { label: "First name", key: "firstName" },
      { label: "Surname", key: "surname" },
      { label: "Mobile", key: "mobile" },
      { label: "Other phone", key: "otherPhone" },
      { label: "Email", key: "email" },
      { label: "Date of birth", key: "dateOfBirth" },
      { label: "Marital status", key: "maritalStatus" },
      { label: "Home owner", key: "homeOwner" },
      { label: "Licence type", key: "typeOfDrivingLicence" },
      { label: "Driving licence number", key: "drivingLicenceNumber" },
      { label: "Test pass date", key: "testPassDate" },
      { label: "Secondary occupation", key: "secondaryOccupation" },
    ],
  },
  {
    section: "History",
    fields: [
      { label: "Has motoring convictions", key: "motoringConvictions" },
      { label: "Has non-motoring convictions", key: "unspentCriminalConvictionsNonMotoring" },
      { label: "Has motor claims", key: "isMotorClaims" },
      { label: "Number of motoring convictions", key: "numberOfMotoringConvictions" },
      {
        label: "Claims in last 5 years",
        key: "numberOfAccidentsOrClaimsInLast5Years",
      },
    ],
  },
  {
    section: "Cover",
    fields: [
      { label: "Has medical conditions", key: "haveMedicalConditions" },
      { label: "Medical conditions", key: "kindOfMedicalCondition" },
      { label: "UK resident since birth", key: "isUKResidentSinceBirth" },
      { label: "Date moved to UK", key: "dateMovedToUK" },
      { label: "Uses another vehicle", key: "useOtherVehicle" },
      { label: "Other vehicle owner", key: "theOwnerOfOtherVehicle" },
      { label: "Access to another vehicle", key: "accessToOtherVehicle" },
      { label: "Years driving vehicle", key: "howManyYearsDrivingVehicle" },
      { label: "Letter from employer available", key: "canGetLetterFromEmployer" },
      { label: "Other vehicle type", key: "theOtherVehicleType" },
      {
        label: "Years driving spouse vehicle",
        key: "howManyYearsDrivingSpouseVehicle",
      },
      { label: "NCB years on spouse vehicle", key: "nbcYearsOnSpouseVehicle" },
      { label: "Main driver", key: "isMainDriver" },
      { label: "Business miles per year", key: "businessMilesPerYear" },
      { label: "Driven for personal use", key: "isVehicleDrivenForPersonalUse" },
      { label: "Additional miles per year", key: "additionalMilesPerYear" },
      {
        label: "Insurance previously refused",
        key: "isOtherVehicleDriverHadInsuranceRefused",
      },
      {
        label: "Reason for refusal",
        key: "reasonForOtherVehicleInsuranceRefused",
      },
      { label: "Has additional driver", key: "additionalDriver" },
    ],
  },
];

export interface ArraySection {
  section: string;
  key: string;
  fields: FieldDefinition[];
}

export const ARRAY_SECTIONS: ArraySection[] = [
  {
    section: "Occupations",
    key: "driverOccupations",
    fields: [
      { label: "Occupation", key: "occupation" },
      { label: "Business sector", key: "businessSector" },
    ],
  },
  {
    section: "Motoring convictions",
    key: "motoringConvictionEntries",
    fields: [
      { label: "Offence code", key: "dvlaMotoringOffenceCode" },
      { label: "Date", key: "dateOfMotoringConviction" },
      { label: "Fine charged", key: "isFineCharged" },
      { label: "Fine amount", key: "fineAmount" },
      { label: "Led to ban", key: "isLeadToDrivingBan" },
      { label: "Ban length", key: "drivingBanLength" },
      { label: "Points", key: "penaltyPointsAwarded" },
    ],
  },
  {
    section: "Non-motoring convictions",
    key: "nonMotoringConvictions",
    fields: [
      { label: "Date", key: "dateOfNonMotoringConviction" },
      { label: "Type", key: "typeOfNonMotoringConviction" },
      { label: "Sentence", key: "sentenceGivenForNonMotoringConviction" },
    ],
  },
  {
    section: "Motor claims",
    key: "motorClaims",
    fields: [
      { label: "Date", key: "dateOfMotorClaim" },
      { label: "Type", key: "motorClaimType" },
      { label: "Kind", key: "kindOfMotorClaim" },
      { label: "At fault", key: "isClaimYourFault" },
      { label: "Affected NCB", key: "isClaimAffectedNoClaimsBonus" },
      { label: "Cost", key: "costofMotorClaim" },
      { label: "Anyone injured", key: "isAnyonePhysicallyInjured" },
      { label: "Whose policy", key: "whosePolicyIsClaimOn" },
    ],
  },
];

export const ADDITIONAL_DRIVER_FIELDS: FieldDefinition[] = [
  { label: "Title", key: "title" },
  { label: "First name", key: "firstName" },
  { label: "Surname", key: "surname" },
  { label: "Relationship", key: "relationship" },
  { label: "Date of birth", key: "dateOfBirth" },
  { label: "Marital status", key: "maritalStatus" },
  { label: "Home owner", key: "homeOwner" },
  { label: "Licence type", key: "typeOfDrivingLicence" },
  { label: "Driving licence number", key: "drivingLicenceNumber" },
  { label: "Test pass date", key: "testPassDate" },
  { label: "Secondary occupation", key: "secondaryOccupation" },
  { label: "Has motoring convictions", key: "motoringConvictions" },
  {
    label: "Has non-motoring convictions",
    key: "unspentCriminalConvictionsNonMotoring",
  },
  { label: "Has motor claims", key: "isMotorClaims" },
  { label: "Has medical conditions", key: "haveMedicalConditions" },
  { label: "Medical conditions", key: "kindOfMedicalCondition" },
  { label: "UK resident since birth", key: "isUKResidentSinceBirth" },
  { label: "Date moved to UK", key: "dateMovedToUK" },
  { label: "Uses another vehicle", key: "useOtherVehicle" },
  { label: "Other vehicle owner", key: "theOwnerOfOtherVehicle" },
  { label: "Access to another vehicle", key: "accessToOtherVehicle" },
  { label: "Years driving vehicle", key: "howManyYearsDrivingVehicle" },
  { label: "Letter from employer available", key: "canGetLetterFromEmployer" },
  { label: "Other vehicle type", key: "theOtherVehicleType" },
  {
    label: "Years driving spouse vehicle",
    key: "howManyYearsDrivingSpouseVehicle",
  },
  { label: "NCB years on spouse vehicle", key: "nbcYearsOnSpouseVehicle" },
  { label: "Business miles per year", key: "businessMilesPerYear" },
  { label: "Driven for personal use", key: "isVehicleDrivenForPersonalUse" },
  { label: "Additional miles per year", key: "additionalMilesPerYear" },
  {
    label: "Insurance previously refused",
    key: "isOtherVehicleDriverHadInsuranceRefused",
  },
  {
    label: "Reason for refusal",
    key: "reasonForOtherVehicleInsuranceRefused",
  },
];

/** Every key placed by a section above, used to find the leftovers. */
export const PLACED_KEYS = new Set<string>([
  ...MAIN_SECTIONS.flatMap((section) => section.fields.map((field) => field.key)),
  ...ARRAY_SECTIONS.map((section) => section.key),
  "additionalDrivers",
  "id",
  "isMarketPlaceFormChecker",
  "__capturedFields",
]);
